import { useApi, useToaster } from "@shopexperts/shared";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import type {
  AdminReconcileAllWalletAllocationsSummary,
  AdminReconcileWalletAllocationsSummary,
} from "@shopexperts/legacy-api";
import { formatCurrency } from "@/v2Projects/utils/display";

interface ReconcileAdminWalletAllocationsInput {
  organizationId: number;
}

export function useReconcileAdminWalletAllocations() {
  const api = useApi();
  const toaster = useToaster();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["admin", "wallet", "reconcileAllocations"],
    mutationFn: async ({ organizationId }: ReconcileAdminWalletAllocationsInput) => {
      return await api.admin.wallet.reconcileOrganizationAllocations({
        params: { organization: organizationId },
      });
    },
    onSuccess: (summary: AdminReconcileWalletAllocationsSummary, variables) => {
      const parts = [`Linked ${formatCurrency(summary.allocated_cents, "USD")} to funding sources.`];
      if (summary.allocations_purged > 0) {
        parts.push(`Purged ${summary.allocations_purged} stale link${summary.allocations_purged === 1 ? "" : "s"}.`);
      }
      if (summary.shortfall_cents > 0) {
        parts.push(`${formatCurrency(summary.shortfall_cents, "USD")} could not be matched to a source.`);
      }
      toaster.create({
        title: "Wallet allocations reconciled",
        description: parts.join(" "),
        type: summary.shortfall_cents > 0 ? "warning" : "success",
      });

      queryClient.invalidateQueries({ queryKey: ["admin", "wallet", "transactions"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "wallet", "organization", variables.organizationId] });
    },
    onError: (error: Error) => {
      toaster.create({ title: `Failed to reconcile wallet allocations: ${error.message}`, type: "error" });
    },
  });
}

export function useReconcileAllAdminWalletAllocations() {
  const api = useApi();
  const toaster = useToaster();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["admin", "wallet", "reconcileAllAllocations"],
    mutationFn: async () => {
      return await api.admin.wallet.reconcileAllAllocations();
    },
    onSuccess: (summary: AdminReconcileAllWalletAllocationsSummary) => {
      const wallets = `${summary.wallets_reconciled} wallet${summary.wallets_reconciled === 1 ? "" : "s"}`;
      const parts = [
        `Checked ${wallets} and linked ${formatCurrency(summary.allocated_cents, "USD")} to funding sources.`,
      ];
      if (summary.allocations_purged > 0) {
        parts.push(`Purged ${summary.allocations_purged} stale link${summary.allocations_purged === 1 ? "" : "s"}.`);
      }
      if (summary.shortfall_cents > 0) {
        parts.push(`${formatCurrency(summary.shortfall_cents, "USD")} could not be matched to a source.`);
      }
      toaster.create({
        title: "Wallet allocations reconciled",
        description: parts.join(" "),
        type: summary.shortfall_cents > 0 ? "warning" : "success",
      });

      queryClient.invalidateQueries({ queryKey: ["admin", "wallet"] });
    },
    onError: (error: Error) => {
      toaster.create({ title: `Failed to reconcile wallet allocations: ${error.message}`, type: "error" });
    },
  });
}
